import React, { useState, useEffect } from "react";
import { useInput, useTranslate } from "react-admin";
import {
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  Paper,
  Typography,
  CardHeader,
  Divider,
  InputLabel,
  FormControl,
  FormHelperText,
  TextField,
  InputAdornment,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

const CustomSelect2 = ({
  data = [],
  multiple = true,
  optionText = "name",
  optionDisabled = "disabled",
  renderItem = null,
  renderDialogExtra = null,
  label,
  ...props
}) => {
  const translate = useTranslate();
  const { onChange, onBlur, ...rest } = props;
  const {
    field,
    fieldState: { isTouched, invalid, error },
    formState: { isSubmitted },
    isRequired,
  } = useInput({ onChange, onBlur, ...props });

  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [leftFilter, setLeftFilter] = useState("");
  const [rightFilter, setRightFilter] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Inicializa o estado com valores e dados existentes
  useEffect(() => {
    if (data && data.length > 0) {
      const currentValues = Array.isArray(field.value)
        ? field.value
        : field.value
          ? [field.value]
          : [];

      // field.value geralmente contém IDs.
      // Precisamos corresponder esses IDs aos objetos em `data`.
      // Usando String(id) para comparação para lidar com possíveis incompatibilidades de tipo (número vs string)
      const selectedIds = new Set(
        currentValues.map((v) =>
          typeof v === "object" && v !== null ? String(v.id) : String(v),
        ),
      );

      const newRight = data.filter((item) => selectedIds.has(String(item.id)));
      const newLeft = data.filter((item) => !selectedIds.has(String(item.id)));

      setLeft(newLeft);
      setRight(newRight);
      setInitialized(true);
    } else if (data && data.length === 0) {
      setLeft([]);
      setRight([]);
      setInitialized(true);
    }
  }, [data, JSON.stringify(field.value)]);
  // Usamos JSON.stringify para evitar loops infinitos se field.value for um novo array refinado a cada renderização,
  // embora a lógica existente deva ser estável.
  // No entanto, se mudarmos `left` e `right`, não queremos reativar este efeito apenas com base na mudança de field.value
  // iniciada por este componente.
  // Idealmente, sincronizamos apenas com as props quando `data` muda ou se `field.value` muda externamente.
  // Mas distinguir mudança externa vs interna é difícil.
  // Por enquanto, vamos supor que `data` é o principal driver para recálculo ou se for a *primeira* carga.

  // Melhor abordagem para inicialização para evitar substituir o estado local em cada renderização:
  // Atualizar apenas se os dados subjacentes mudarem significativamente ou se as verificação implicarem fora de sincronia?
  // Na verdade, confiar em `field.value` é arriscado se também estivermos atualizando-o.
  // Padrão padrão: Sincronizar o estado interno com as props apenas se as props mudarem e diferirem do equivalente do estado interno.

  // Estado do Dialog
  const [open, setOpen] = useState(false);
  const [tempChecked, setTempChecked] = useState([]);
  const [tempLeft, setTempLeft] = useState([]);
  const [tempRight, setTempRight] = useState([]);

  // Sincroniza o estado do diálogo ao abrir
  const handleOpen = () => {
    setTempLeft([...left]);
    setTempRight([...right]);
    setTempChecked([]); // Reseta os itens marcados na lista de transferência ao abrir
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    // Confirma as alterações
    setLeft(tempLeft);
    setRight(tempRight);

    // Atualiza o valor do campo
    updateFieldValue(tempRight);
    setOpen(false);
  };

  // Manipuladores para a lista de transferência dentro do Dialog (operando no estado temporário)
  const handleTempToggle = (value) => () => {
    const currentIndex = tempChecked.indexOf(value);
    const newChecked = [...tempChecked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setTempChecked(newChecked);
  };

  const numberOfTempChecked = (items) =>
    intersection(tempChecked, items).length;

  const handleTempToggleAll = (items) => () => {
    if (numberOfTempChecked(items) === items.length) {
      setTempChecked(not(tempChecked, items));
    } else {
      setTempChecked(union(tempChecked, items));
    }
  };

  const handleTempCheckedRight = () => {
    const leftChecked = intersection(tempChecked, tempLeft);
    const newRight = tempRight.concat(leftChecked);
    const newLeft = not(tempLeft, leftChecked);

    setTempRight(newRight);
    setTempLeft(newLeft);
    setTempChecked(not(tempChecked, leftChecked));
  };

  const handleTempCheckedLeft = () => {
    const rightChecked = intersection(tempChecked, tempRight);
    const newLeft = tempLeft.concat(rightChecked);
    const newRight = not(tempRight, rightChecked);

    setTempLeft(newLeft);
    setTempRight(newRight);
    setTempChecked(not(tempChecked, rightChecked));
  };

  // Atualiza o campo do react-hook-form com o valor apropriado (IDs)
  const updateFieldValue = (newRightItems) => {
    const valueToStore = newRightItems.map((item) => item.id);
    const finalValue = multiple
      ? valueToStore
      : valueToStore.length > 0
        ? valueToStore[0]
        : null;

    field.onChange(finalValue);
    field.onBlur(finalValue);
  };

  const getOptionText = (item) => {
    if (typeof optionText === "function") {
      return optionText(item);
    }
    // Lida com notação de ponto como "category.name"
    return optionText.split(".").reduce((acc, part) => acc && acc[part], item);
  };

  const getOptionDisabled = (item) => {
    if (typeof optionDisabled === "function") {
      return Boolean(optionDisabled(item));
    }
    return Boolean(item?.[optionDisabled]);
  };

  const renderOptionContent = (item, context = {}) => {
    if (typeof renderItem === "function") {
      return renderItem(item, context);
    }

    return getOptionText(item);
  };

  const renderExtraContent = (context = {}) => {
    if (typeof renderDialogExtra === "function")
      return renderDialogExtra(context);

    return null;
  };

  const filterItems = (items, filter) => {
    if (!filter) return items;
    return items.filter((item) => {
      const text = getOptionText(item);
      return text && String(text).toLowerCase().includes(filter.toLowerCase());
    });
  };

  // customList modificada para usar estado temporário e manipuladores
  const customList = (
    title,
    items,
    filterValue,
    setFilterValue,
    useChip = false,
    singleSelect = false,
  ) => (
    <Paper
      sx={{
        width: "100%",
        height: 400,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e0e0e0",
      }}
    >
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          !singleSelect && (
            <Checkbox
              onClick={handleTempToggleAll(items)}
              checked={
                numberOfTempChecked(items) === items.length &&
                items.length !== 0
              }
              indeterminate={
                numberOfTempChecked(items) !== items.length &&
                numberOfTempChecked(items) !== 0
              }
              disabled={items.length === 0}
              inputProps={{ "aria-label": "all items selected" }}
            />
          )
        }
        title={title}
        subheader={
          !singleSelect
            ? `${numberOfTempChecked(items)}/${items.length} selecionado(s)`
            : `${items.length} opções`
        }
      />
      <Divider />
      <Box sx={{ p: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Buscar..."
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Divider />
      <List
        dense
        component="div"
        role="list"
        sx={{ width: "100%", flex: 1, overflow: "auto" }}
      >
        {items.map((value) => {
          const labelId = `transfer-list-all-item-${value.id}-label`;
          const isOptionDisabled = getOptionDisabled(value);
          const isSelected = singleSelect
            ? tempRight.some((item) => item.id === value.id)
            : tempChecked.indexOf(value) !== -1;

          return (
            <ListItem
              key={value.id}
              role="listitem"
              button
              disabled={isOptionDisabled}
              onClick={
                singleSelect
                  ? () => {
                      if (!isOptionDisabled) setTempRight([value]);
                    }
                  : handleTempToggle(value)
              }
            >
              <ListItemIcon>
                {singleSelect ? (
                  <Radio
                    checked={isSelected}
                    disabled={isOptionDisabled}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-labelledby": labelId }}
                  />
                ) : (
                  <Checkbox
                    checked={isSelected}
                    disabled={isOptionDisabled}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-labelledby": labelId }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={
                  typeof renderItem === "function"
                    ? renderOptionContent(value, {
                        selected: isSelected,
                        disabled: isOptionDisabled,
                        multiple,
                        panel: useChip ? "right" : "left",
                      })
                    : useChip
                      ? <Chip label={getOptionText(value)} size="small" />
                      : renderOptionContent(value, {
                          selected: isSelected,
                          disabled: isOptionDisabled,
                          multiple,
                          panel: "left",
                        })
                }
              />
            </ListItem>
          );
        })}
        <ListItem />
      </List>
    </Paper>
  );

  const filteredLeft = filterItems(tempLeft, leftFilter);
  const filteredRight = filterItems(tempRight, rightFilter);
  // Para seleção única, filtramos 'data' diretamente
  const filteredData = filterItems(data, leftFilter); // Reutiliza leftFilter para a pesquisa do painel único

  const leftChecked = intersection(tempChecked, tempLeft);
  const rightChecked = intersection(tempChecked, tempRight);
  const enabledLeftItems = filteredLeft.filter((item) => !getOptionDisabled(item));
  const enabledLeftChecked = intersection(tempChecked, enabledLeftItems);

  return (
    <>
      <FormControl
        fullWidth
        error={(isTouched || isSubmitted) && invalid}
        {...rest}
        component="fieldset"
        sx={{ mt: 2 }}
        onClick={handleOpen}
      >
        <InputLabel
          shrink
          required={isRequired}
          sx={{
            position: "relative",
            transform: "none",
            mb: 1,
            fontSize: "1rem",
            fontWeight: 500,
          }}
        >
          {label}
        </InputLabel>

        <Box
          sx={{
            border: (theme) =>
              `1px solid ${(isTouched || isSubmitted) && invalid ? theme.palette.error.main : "rgba(0, 0, 0, 0.23)"}`,
            borderRadius: 1,
            p: 1,
            // minHeight: "56px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            "&:hover": {
              borderColor: "text.primary",
            },
          }}
        >
          {right.length === 0 ? (
            <Typography
              color="textSecondary"
              variant="body1"
              sx={{ alignSelf: "center" }}
            >
              Selecione...
            </Typography>
          ) : (
            right.map((item) => (
              <Chip key={item.id} label={getOptionText(item)} size="small" />
            ))
          )}
        </Box>
        {(isTouched || isSubmitted) && invalid && (
          <FormHelperText error>{error.message}</FormHelperText>
        )}
      </FormControl>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        disableEnforceFocus
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {label}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderExtraContent({
            data,
            selected: right,
            available: left,
            multiple,
          })}
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            {multiple ? (
              <>
                <Grid item xs={12} md>
                  {customList(
                    "Disponíveis",
                    filteredLeft,
                    leftFilter,
                    setLeftFilter,
                  )}
                </Grid>
                <Grid item xs={12} md="auto">
                  <Grid container direction="column" alignItems="center">
                    <Button
                      sx={{ my: 0.5 }}
                      variant="outlined"
                      size="small"
                      onClick={handleTempCheckedRight}
                      disabled={enabledLeftChecked.length === 0}
                      aria-label="move selected right"
                    >
                      &gt;
                    </Button>
                    <Button
                      sx={{ my: 0.5 }}
                      variant="outlined"
                      size="small"
                      onClick={handleTempCheckedLeft}
                      disabled={rightChecked.length === 0}
                      aria-label="move selected left"
                    >
                      &lt;
                    </Button>
                  </Grid>
                </Grid>
                <Grid item xs={12} md>
                  {customList(
                    "Selecionados",
                    filteredRight,
                    rightFilter,
                    setRightFilter,
                    true,
                  )}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                {customList(
                  "Selecione uma opção",
                  filteredData,
                  leftFilter,
                  setLeftFilter,
                  false,
                  true, // modo de seleção única
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleConfirm} variant="contained" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomSelect2;
